import { useEffect, useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { MaterialSelect } from "@/components/slot/MaterialSelect";
import {
  useCreateMaterialSet,
  useUpdateMaterialSet,
} from "@/api/hooks/useCennik";
import { api, extractErrorMessage } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import { toast } from "@/components/ui/use-toast";
import { formatEur, formatPct, materialPrimaryId, normalizeNum } from "@/lib/format";
import type { DataResponse, Material } from "@/api/schemas";

/** Referencia na komponent setu uložená v ``supplier_data.set_components``. */
export interface SetComponentRef {
  material_id: string;
  nazov: string;
  kod?: string | null;
  /** Kusy v jednom sete (KOLO 228); staršie sety ho nemajú — 1 ks. */
  pocet?: number;
}

/**
 * Bezpečne vyparsuje komponenty setu zo ``supplier_data`` — backend môže
 * vrátiť objekt aj JSON string (podľa DB driveru).
 */
export function parseSetComponents(
  material: Material | null | undefined,
): SetComponentRef[] {
  const raw = material?.supplier_data as unknown;
  if (!raw) return [];
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  const comps = (parsed as { set_components?: unknown } | null)
    ?.set_components;
  if (!Array.isArray(comps)) return [];
  const platne = comps.filter(
    (c): c is SetComponentRef =>
      !!c &&
      typeof c === "object" &&
      typeof (c as SetComponentRef).material_id === "string",
  );
  // Starší set nesie ten istý komponent dvakrát namiesto počtu — zlož
  // ho do jedného riadku s kusmi (KOLO 228)
  const zlozene = new Map<string, SetComponentRef>();
  for (const c of platne) {
    const ks = Number(c.pocet ?? 1) || 1;
    const bol = zlozene.get(c.material_id);
    zlozene.set(c.material_id, bol ? { ...bol, pocet: (bol.pocet ?? 1) + ks } : { ...c, pocet: ks });
  }
  return [...zlozene.values()];
}

/** „2× Záves + Podložka" — kusy pred názvom len keď je ich viac. */
export function popisSetu(refs: SetComponentRef[]): string {
  return refs
    .map((c) => ((c.pocet ?? 1) > 1 ? `${c.pocet}× ${c.nazov}` : c.nazov))
    .join(" + ");
}

/**
 * Spojiť kovania do setu — jedna cenníková položka z 2+ komponentov
 * (Axilo noha + podložka, Blum záves + podložka…). Nákup = súčet,
 * réžia + PRÁCA z prvého (hlavného) komponentu — práca sa účtuje raz
 * za set, nie per komponent.
 *
 * S ``editMaterial`` beží v edit móde: prefill názvu + komponentov zo
 * ``supplier_data.set_components`` a submit ide cez PATCH …/set.
 */
export function KovanieSetDialog({
  open,
  onOpenChange,
  editMaterial,
  duplicateFrom,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existujúci set na úpravu — bez neho dialóg vytvára nový set. */
  editMaterial?: Material;
  /** Predloha na duplikovanie — prefill ako edit, ale uloží NOVÝ set. */
  duplicateFrom?: Material;
}): JSX.Element {
  const isEdit = !!editMaterial;
  // zdroj prefillu (edit alebo duplikát)
  const source = editMaterial ?? duplicateFrom;
  const createMutation = useCreateMaterialSet();
  const updateMutation = useUpdateMaterialSet();
  // Komponenty v poradí — prvý je hlavný (réžia + práca)
  const [komponenty, setKomponenty] = useState<(Material | null)[]>([
    null,
    null,
  ]);
  // Kusy komponentu v jednom sete (KOLO 228 — user: „keď združené kovanie
  // vytváram, nech ide meniť počty kusov"), text — pole sa smie vymazať
  const [kusy, setKusy] = useState<string[]>(["1", "1"]);
  const [nazov, setNazov] = useState(editMaterial?.nazov ?? "");
  const [nazovTouched, setNazovTouched] = useState(!!editMaterial);
  const [prefilled, setPrefilled] = useState(false);
  // Ceny setu: nákup "" = auto (súčet komponentov); réžia a práca sa
  // účtujú raz za CELÝ set
  const [nakup, setNakup] = useState("");
  const [rezia, setRezia] = useState("");
  const [praca, setPraca] = useState("");

  // Edit/duplikát: komponenty zo supplier_data → detaily ako Material objekty
  const editComponents = useMemo(
    () => parseSetComponents(source),
    [source],
  );
  const componentQueries = useQueries({
    queries: (open && source ? editComponents : []).map((c) => ({
      queryKey: queryKeys.cennik.material(c.material_id),
      queryFn: async (): Promise<DataResponse<Material>> => {
        const { data } = await api.get<DataResponse<Material>>(
          `/cennik/materialy/${c.material_id}`,
        );
        return data;
      },
    })),
  });

  useEffect(() => {
    if (open) {
      setKomponenty([null, null]);
      setKusy(["1", "1"]);
      setNazov(
        editMaterial?.nazov ??
          (duplicateFrom ? `${duplicateFrom.nazov} (kópia)` : ""),
      );
      // Pri prefille (edit/duplikát) sa názov neprepisuje z komponentov
      setNazovTouched(!!source);
      setPrefilled(false);
      // Nákup prefillni len ak bol nastavený manuálne (inak auto);
      // réžiu a prácu vždy — pošlú sa explicitne (zachovajú sa)
      const sd = source?.supplier_data as
        | { cena_manualna?: boolean }
        | null
        | undefined;
      setNakup(
        source && sd?.cena_manualna ? normalizeNum(source.nakup_cena) : "",
      );
      setRezia(source ? normalizeNum(source.rezia_pct) : "");
      setPraca(source ? normalizeNum(source.praca_eur_per_mj) : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editMaterial, duplicateFrom]);

  // Prefill komponentov po načítaní detailov (iba raz na otvorenie).
  // `loadedIds` v deps — pole queries je nová referencia každý render.
  const loadedComponents = componentQueries.map((q) => q.data?.data ?? null);
  const loadedIds = loadedComponents.map((m) => m?.id ?? "").join(",");
  useEffect(() => {
    if (!open || !source || prefilled) return;
    if (
      loadedComponents.length >= 1 &&
      loadedComponents.every((m): m is Material => m != null)
    ) {
      setKomponenty(loadedComponents);
      setKusy(editComponents.map((c) => String(c.pocet ?? 1)));
      setPrefilled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source, prefilled, loadedIds]);

  const vybrane = useMemo(
    () => komponenty.filter((k): k is Material => k != null),
    [komponenty],
  );
  /** Kusy riadku `i` — prázdne alebo nezmysel = 1. */
  const ksRiadku = (i: number): number => Math.max(1, parseInt(kusy[i] ?? "1", 10) || 1);
  const vybraneSKusmi = komponenty
    .map((k, i) => (k ? { material: k, ks: ksRiadku(i) } : null))
    .filter((x): x is { material: Material; ks: number } => x != null);
  const kusovSpolu = vybraneSKusmi.reduce((acc, x) => acc + x.ks, 0);

  // Prefill názvu zo spojených názvov, kým ho používateľ neprepíše.
  // V edit móde nikdy — názov drží hodnotu z editMaterial.
  useEffect(() => {
    if (!isEdit && !nazovTouched) {
      setNazov(
        vybraneSKusmi.map((x) => (x.ks > 1 ? `${x.ks}× ${x.material.nazov}` : x.material.nazov)).join(" + "),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, vybrane, nazovTouched, kusy.join(",")]);

  const sumaNakup = vybraneSKusmi.reduce(
    (acc, x) => acc + (parseFloat(String(x.material.nakup_cena)) || 0) * x.ks,
    0,
  );
  const hlavny = vybrane[0] ?? null;
  const isPending = createMutation.isPending || updateMutation.isPending;

  function setKomponent(i: number, m: Material | null): void {
    setKomponenty((prev) => {
      const next = [...prev];
      next[i] = m;
      return next;
    });
  }

  async function handleSubmit(): Promise<void> {
    if (kusovSpolu < 2 || !nazov.trim()) return;
    const num = (v: string): string => v.replace(",", ".").trim();
    const payload = {
      nazov: nazov.trim(),
      component_ids: vybraneSKusmi.map((x) => x.material.id),
      pocty: vybraneSKusmi.map((x) => x.ks),
      // "" = auto (nákup zo súčtu, réžia/práca z hlavného komponentu)
      ...(num(nakup) ? { nakup_cena: num(nakup) } : {}),
      ...(num(rezia) ? { rezia_pct: num(rezia) } : {}),
      ...(num(praca) ? { praca_eur_per_mj: num(praca) } : {}),
    };
    try {
      if (editMaterial) {
        await updateMutation.mutateAsync({ id: editMaterial.id, ...payload });
        toast({
          title: "Set upravený",
          description: `${payload.nazov} — ${kusovSpolu} ks, nákup ${formatEur(sumaNakup)}`,
        });
      } else {
        await createMutation.mutateAsync(payload);
        toast({
          title: "Set vytvorený",
          description: `${payload.nazov} — ${kusovSpolu} ks, nákup ${formatEur(sumaNakup)}`,
        });
      }
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: isEdit ? "Chyba pri úprave setu" : "Chyba pri vytváraní setu",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? "Upraviť set"
              : duplicateFrom
                ? "Duplikovať set"
                : "Spojiť kovania do setu"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Set spojí viac kovaní do jednej položky s vlastnou cenou.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {komponenty.map((k, i) => (
            <div key={i} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>
                  {i === 0 ? "Hlavný komponent (réžia + práca)" : `Komponent ${i + 1}`}
                </Label>
                {komponenty.length > 1 ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0"
                    aria-label={`Odobrať komponent ${i + 1}`}
                    onClick={() => {
                      setKomponenty((prev) => prev.filter((_, j) => j !== i));
                      setKusy((prev) => prev.filter((_, j) => j !== i));
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </div>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <MaterialSelect
                    value={k?.id ?? null}
                    onChange={(_, m) => setKomponent(i, m ?? null)}
                    druh="kusovy"
                    sekcia="kovanie"
                    placeholder="Vyhľadať kovanie (názov, kód, dekor)…"
                    // Komponentov je pod sebou viac — rozbalený je len ten,
                    // do ktorého sa píše (ako pickery v Upraviť materiál)
                    kompaktny
                  />
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Input
                    type="number"
                    min={1}
                    max={999}
                    inputMode="numeric"
                    aria-label={`Počet kusov komponentu ${i + 1}`}
                    value={kusy[i] ?? "1"}
                    onChange={(e) =>
                      setKusy((prev) => {
                        const next = [...prev];
                        next[i] = e.target.value;
                        return next;
                      })
                    }
                    className="h-9 w-16 text-right font-mono"
                  />
                  <span className="text-xs text-muted-foreground">ks</span>
                </div>
              </div>
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setKomponenty((prev) => [...prev, null]);
              setKusy((prev) => [...prev, "1"]);
            }}
          >
            <Plus className="h-4 w-4" />
            Pridať komponent
          </Button>

          <Separator />

          <div className="space-y-1.5">
            <Label htmlFor="set-nazov">Názov setu</Label>
            <Input
              id="set-nazov"
              value={nazov}
              onChange={(e) => {
                setNazov(e.target.value);
                setNazovTouched(true);
              }}
              placeholder="napr. noha AXILO 125 + podložka"
              maxLength={255}
            />
          </div>

          {/* Cena setu — nákup override + práca/réžia raz za celý set */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="set-nakup">Nákup €/set</Label>
              <Input
                id="set-nakup"
                inputMode="decimal"
                value={nakup}
                onChange={(e) => setNakup(e.target.value)}
                placeholder={
                  sumaNakup > 0 ? `auto: ${formatEur(sumaNakup)}` : "auto: súčet"
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="set-rezia">Réžia %</Label>
              <Input
                id="set-rezia"
                inputMode="decimal"
                value={rezia}
                onChange={(e) => setRezia(e.target.value)}
                placeholder={
                  hlavny ? `auto: ${normalizeNum(hlavny.rezia_pct)}` : "auto"
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="set-praca">Práca €/set</Label>
              <Input
                id="set-praca"
                inputMode="decimal"
                value={praca}
                onChange={(e) => setPraca(e.target.value)}
                placeholder={
                  hlavny
                    ? `auto: ${normalizeNum(hlavny.praca_eur_per_mj)}`
                    : "auto"
                }
              />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Práca a réžia sa účtujú raz za celý set. Prázdne pole = auto
            (nákup zo súčtu komponentov, réžia a práca z hlavného).
            Manuálny nákup sa pri aktualizácii cenníkov neprepíše.
          </p>

          {vybrane.length > 0 ? (
            <div className="rounded-md border bg-background-alt/40 p-3 text-xs space-y-1">
              {vybraneSKusmi.map(({ material: m, ks }, i) => (
                <div key={`${m.id}-${i}`} className="flex justify-between gap-2">
                  <span className="truncate">
                    {ks > 1 ? `${ks}× ` : ""}
                    {m.nazov}
                    <span className="ml-1.5 font-mono text-[10px] text-muted-foreground">
                      {materialPrimaryId(m) ?? ""}
                    </span>
                  </span>
                  <span className="shrink-0 font-mono">
                    {ks > 1 ? `${ks} × ${formatEur(m.nakup_cena)} = ` : ""}
                    {formatEur((parseFloat(String(m.nakup_cena)) || 0) * ks)}
                  </span>
                </div>
              ))}
              <Separator />
              <div className="flex justify-between font-medium">
                <span>Nákup spolu</span>
                <span className="font-mono" data-testid="set-suma">
                  {formatEur(sumaNakup)}
                </span>
              </div>
              {hlavny ? (
                <p className="text-[11px] text-muted-foreground">
                  Réžia {formatPct(hlavny.rezia_pct)} a práca{" "}
                  {formatEur(hlavny.praca_eur_per_mj)}/set z hlavného
                  komponentu — práca sa účtuje raz za set.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Zrušiť
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={isPending || kusovSpolu < 2 || !nazov.trim()}
            title={kusovSpolu < 2 ? "Set potrebuje aspoň 2 kusy — pridaj komponent alebo zvýš počet" : undefined}
          >
            {isEdit
              ? isPending
                ? "Ukladá sa…"
                : "Uložiť set"
              : isPending
                ? "Vytvára sa…"
                : "Vytvoriť set"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
